import { z } from 'zod';

// Esquemas de identidad y cuenta (RF-01, RF-07.05). Las reglas finas (longitudes,
// formato del código de invitación) las decide el servidor y vuelven como errores.

export const CSRF_HEADER = 'x-csrf-token';

export const csrfResponseSchema = z.object({ csrfToken: z.string() });

export const userSchema = z
  .object({
    id: z.uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    skoolName: z.string(),
    createdAt: z.iso.datetime(),
    mfaEnabled: z.boolean(),
  })
  .meta({ id: 'User' });

export type User = z.infer<typeof userSchema>;

export const signupRequestSchema = z.object({
  email: z.string().max(320),
  password: z.string().max(1024),
  firstName: z.string().max(200),
  lastName: z.string().max(200),
  skoolName: z.string().max(300),
  invitationCode: z.string().max(100),
  /** Casilla no premarcada (RF-01.01). */
  termsAccepted: z.boolean(),
  termsVersion: z.string().max(40),
});

export const signupStatusSchema = z.object({
  email: z.string(),
  expiresIn: z.number().int(),
  retryAfter: z.number().int(),
  /** El correo no se pudo confirmar; si llega, el código sirve. */
  mailUncertain: z.boolean(),
});

export const signupResendSchema = z.object({ email: z.string().max(320).optional() });
export const codeSchema = z.object({ code: z.string().max(20) });

export const loginRequestSchema = z.object({
  email: z.string().max(320),
  password: z.string().max(1024),
});

export const loginResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('authenticated'), user: userSchema, csrfToken: z.string() }),
  z.object({ status: z.literal('mfa_required'), csrfToken: z.string() }),
]);

export const authenticatedResponseSchema = z.object({ user: userSchema, csrfToken: z.string() });

export const meResponseSchema = z.object({
  user: userSchema,
  mfa: z.object({ enabled: z.boolean(), verifiedInSession: z.boolean() }),
  /** RF-01.28: la interfaz se adapta con esto; la autorización real se repite en el servidor. */
  access: z.object({
    platform: z.boolean(),
    blocked: z.boolean(),
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
    /** Permisos de administración que exigen activar o superar la verificación en dos pasos. */
    requiresMfa: z.array(z.string()),
  }),
});

export const invitationRedeemSchema = z.object({ code: z.string().max(100) });
export const invitationRedeemResponseSchema = z.object({ alreadyActive: z.boolean() });

export const profileUpdateSchema = z.object({
  firstName: z.string().max(200),
  lastName: z.string().max(200),
  skoolName: z.string().max(300),
});

export const passwordChangeSchema = z.object({
  currentPassword: z.string().max(1024),
  newPassword: z.string().max(1024),
});

export const passwordForgotSchema = z.object({ email: z.string().max(320) });
export const passwordResetSchema = z.object({
  token: z.string().max(200),
  password: z.string().max(1024),
});

export const messageResponseSchema = z.object({ message: z.string() });

export const sessionSchema = z.object({
  id: z.uuid(),
  device: z.string(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  current: z.boolean(),
});

export type AccountSession = z.infer<typeof sessionSchema>;

export const totpSetupResponseSchema = z.object({ secret: z.string(), otpauthUrl: z.string() });
export const totpDisableSchema = z.object({
  password: z.string().max(1024),
  code: z.string().max(20),
});
